import { useEffect, useRef } from 'react';
import { useAnswerQuestionMutation } from '../apis';
import { getToken } from '../utils/auth';
import { peekPendingVote, clearPendingVote } from '../utils/pendingVote';

// Completes the "vote first, lock on login" flow. When a logged-out user taps a
// vote, QuestionCard stashes it and routes them through auth. On the next load
// where they're authenticated, replay that vote once so the opinion they already
// expressed is recorded without making them hunt down the question again.
//
// Mounted at the app root. The login flow reloads the page, so this runs fresh
// and catches that case; signup (no reload) replays inline in the form.
//
// Replay uses mode: 'set' — an idempotent lock-in. The interactive /answer is a
// TOGGLE, so a plain replay could cancel an existing matching vote, and a
// read-then-write check would still race with interactive voting (the feed is
// live during replay). 'set' ensures the vote equals the intent and never
// cancels it, so it is safe no matter what the current state is or what votes
// interleave. The intent is cleared only once resolved; a failed call leaves it
// for the next load to retry (bounded by its one-hour expiry).
export const usePendingVoteReplay = (): void => {
  const [answerQuestion] = useAnswerQuestionMutation();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!getToken()) return;
    const pending = peekPendingVote();
    if (!pending) return;

    answerQuestion({ ...pending, mode: 'set' })
      .unwrap()
      .then(() => clearPendingVote())
      .catch(() => {
        // Network/API failure: leave the intent in place so a later load can
        // retry. The one-hour expiry stops it from lingering indefinitely.
      });
  }, [answerQuestion]);
};
